import useModuleFilters from '../../../shared/data/useModuleFilters';
import FilterBar from '../../../shared/components/patterns/FilterBar';
import { userFormSchema } from '../adminFilter.schemas';
import DataTable from '../../../shared/components/patterns/DataTable';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRoutePath, ROUTE_IDS } from '@/app/router/route-registry';
import {
  createTenantUser,
  listTenantUsers,
  updateTenantUserStatus,
} from '@/modules/admin/services/admin-tenant-users.service';
const USER_ROLE_OPTIONS = ['OWNER', 'ADMIN', 'STAFF'];
const INITIAL_FORM_STATE = {
  fullName: '',
  email: '',
  password: '',
  role: 'ADMIN',
};
export default function AdminTenantUsersPage() {
  const { tenantId = '' } = useParams();
  const tenantsPath = getRoutePath(ROUTE_IDS.ADMIN_TENANTS) || '/admin/tenants';
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pageTitle = useMemo(() => `Utenti accesso tenant: ${tenantId}`, [tenantId]);
  useEffect(() => {
    let active = true;
    async function loadUsers() {
      setIsLoading(true);
      const result = await listTenantUsers(tenantId);
      if (!active) {
        return;
      }
      if (!result.ok) {
        setUsers([]);
        setError(result.message);
        setIsLoading(false);
        return;
      }
      setUsers(result.data.users);
      setError('');
      setIsLoading(false);
    }
    loadUsers();
    return () => {
      active = false;
    };
  }, [tenantId]);
  function updateFormField(field, value) {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  }
  function validateForm() {
    if (!formState.fullName.trim()) {
      return 'Inserisci nome e cognome.';
    }
    if (!formState.email.trim()) {
      return 'Inserisci email.';
    }
    if (!formState.password.trim()) {
      return 'Inserisci password.';
    }
    if (!USER_ROLE_OPTIONS.includes(formState.role)) {
      return 'Ruolo non valido.';
    }
    return '';
  }
  async function reloadUsers() {
    const result = await listTenantUsers(tenantId);
    if (!result.ok) {
      setError(result.message);
      return false;
    }
    setUsers(result.data.users);
    setError('');
    return true;
  }
  async function handleCreateUser(event) {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setIsSubmitting(true);
    const result = await createTenantUser(tenantId, {
      fullName: formState.fullName.trim(),
      email: formState.email.trim(),
      password: formState.password,
      role: formState.role,
    });
    setIsSubmitting(false);
    if (!result.ok) {
      setFormError(result.message);
      return;
    }
    await reloadUsers();
    setFormState(INITIAL_FORM_STATE);
    setFormSuccess('Utente tenant creato correttamente.');
  }
  async function handleToggleUserStatus(user) {
    setPendingUserId(String(user.id));
    setError('');
    const result = await updateTenantUserStatus(tenantId, user.id, !user.active);
    setPendingUserId('');
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setUsers((currentUsers) =>
      currentUsers.map((currentUser) => (currentUser.id === user.id ? result.data.user : currentUser))
    );
  }
  const moduleFilters = useModuleFilters({ schema: userFormSchema, rows: users });
  const filteredRows = moduleFilters.filterRows(users);

  return (
    <section className="admin-tenants admin-tenant-users">
      <div className="admin-tenants__header">
        <div>
          <p className="admin-tenants__eyebrow">Tenant Access Users</p>
          <h2 className="admin-tenants__title">{pageTitle}</h2>
          <p className="admin-tenants__description">
            Utenti di accesso del tenant gestiti da piattaforma auth, separati dal personale operativo.
          </p>
        </div>
        <Link className="admin-shell__nav-link" to={tenantsPath}>
          Torna ai tenant
        </Link>
      </div>


        <FilterBar model={moduleFilters} />
<div className="admin-tenant-users__grid">
        <section className="admin-tenant-users__panel">
          <h3 className="admin-tenant-users__panel-title">Nuovo utente tenant</h3>
          <p className="admin-tenant-users__panel-copy">
            Crea un accesso applicativo per il tenant corrente.
          </p>

          <form className="admin-tenant-users__form" onSubmit={handleCreateUser}>
            <label className="admin-tenant-users__field">
              <span>Nome completo</span>
              <input
                type="text"
                value={formState.fullName}
                onChange={(event) => updateFormField('fullName', event.target.value)}
                disabled={isSubmitting}
              />
            </label>

            <label className="admin-tenant-users__field">
              <span>Email</span>
              <input
                type="email"
                value={formState.email}
                onChange={(event) => updateFormField('email', event.target.value)}
                disabled={isSubmitting}
              />
            </label>

            <label className="admin-tenant-users__field">
              <span>Password iniziale</span>
              <input
                type="password"
                value={formState.password}
                onChange={(event) => updateFormField('password', event.target.value)}
                disabled={isSubmitting}
              />
            </label>

            <label className="admin-tenant-users__field">
              <span>Ruolo</span>
              <select
                value={formState.role}
                onChange={(event) => updateFormField('role', event.target.value)}
                disabled={isSubmitting}
              >
                {USER_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>

            {formError ? (
              <div role="alert" className="admin-tenants__state admin-tenants__state--error wt-inline-error">{formError}</div>
            ) : null}

            {formSuccess ? <div className="admin-tenants__state">{formSuccess}</div> : null}

            <button type="submit" className="admin-tenants__toggle" disabled={isSubmitting}>
              {isSubmitting ? 'Creazione...' : 'Crea utente'}
            </button>
          </form>
        </section>

        <section className="admin-tenant-users__panel">
          <h3 className="admin-tenant-users__panel-title">Utenti del tenant</h3>
          <p className="admin-tenant-users__panel-copy">
            Questi utenti controllano l’accesso al workspace del tenant.
          </p>

          {isLoading ? <div className="admin-tenants__state">Caricamento utenti in corso...</div> : null}

          {!isLoading && error ? (
            <div role="alert" className="admin-tenants__state admin-tenants__state--error wt-inline-error">{error}</div>
          ) : null}

          {!isLoading && !error && users.length === 0 ? (
            <div className="admin-tenants__state">
              Nessun utente di accesso configurato per questo tenant.
            </div>
          ) : null}

          {!isLoading && !error && users.length > 0 ? (
            <div className="admin-tenants__registry">
              <DataTable
                ariaLabel="Utenti workspace"
                rows={filteredRows}
                columns={[
                  {
                    id: 'name',
                    header: 'Nome',
                    render: (user) => {
                      return <>{user.fullName}</>;
                    },
                  },
                  {
                    id: 'email',
                    header: 'Email',
                    render: (user) => {
                      return <>{user.email}</>;
                    },
                  },
                  {
                    id: 'role',
                    header: 'Ruolo',
                    render: (user) => {
                      return <>{user.role}</>;
                    },
                  },
                  {
                    id: 'active',
                    header: 'Stato',
                    render: (user) => {
                      return (
                        <>
                          <span
                            className={
                              user.active
                                ? 'admin-tenants__status admin-tenants__status--active'
                                : 'admin-tenants__status admin-tenants__status--inactive'
                            }
                          >
                            {user.active ? 'Attivo' : 'Disattivato'}
                          </span>
                        </>
                      );
                    },
                  },
                  {
                    id: 'actions',
                    header: 'Azione',
                    render: (user) => {
                      const isPending = pendingUserId === String(user.id);
                      return (
                        <>
                          <button
                            type="button"
                            className="admin-tenants__toggle"
                            onClick={() => handleToggleUserStatus(user)}
                            disabled={isPending}
                          >
                            {isPending ? 'Aggiornamento...' : user.active ? 'Disattiva' : 'Attiva'}
                          </button>
                        </>
                      );
                    },
                  },
                ]}
              />
            </div>
          ) : null}
        </section>
      </div>
    </section>
  );
}
